import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {PopOutWindow} from '@pexip/components';
import {useToggleFullscreen} from '@pexip/hooks';

export {ExternalVideo} from '../views/MeetingVideo/ExternalVideo.view';

import {StreamStatus} from '../types';
import type {MeetingVideoRef} from '../views';
import type {DraggableMeetingVideoProps} from '../views/MeetingVideo/DraggableMeetingVideo.view';
import {DraggableMeetingVideo} from '../views/MeetingVideo/DraggableMeetingVideo.view';
import {ExternalVideo} from '../views/MeetingVideo/ExternalVideo.view';
import {PoppedOutEmptyPresentation} from '../views/PoppedOutEmptyPresentation/PoppedOutEmptyPresentation.view';
import {PresenterLabel} from '../views/PresenterLabel/PresenterLabel.view';

export const PresentationMeetingVideo = React.forwardRef<
    MeetingVideoRef,
    DraggableMeetingVideoProps & {
        presenterText?: string;
        localIsPresenting: boolean;
        localMediaStream?: MediaStream;
        remoteIsPresenting: boolean;
        remoteMediaStream?: MediaStream;
        remotePopOut?: Window | null;
        setIsPresentationBrowserPip: React.Dispatch<
            React.SetStateAction<boolean>
        >;
        setIsPresentationPoppedOut: React.Dispatch<
            React.SetStateAction<boolean>
        >;
    }
>(
    (
        {
            draggableAriaLabel,
            presenterText,
            status,
            localIsPresenting,
            localMediaStream,
            remoteIsPresenting,
            remoteMediaStream,
            remotePopOut,
            handleVideoClick,
            setIsPresentationBrowserPip,
            setIsPresentationPoppedOut,
            isSidePanelVisible,
            floatRoot,
            videoElementId,
            tabIndex,
            onKeyDown,
            ...props
        },
        ref,
    ) => {
        const {t} = useTranslation();
        const toggleFullScreen = useToggleFullscreen();
        const isPresenting = remoteIsPresenting || localIsPresenting;
        const disableDualScreenMode = useCallback(() => {
            setIsPresentationPoppedOut(false);
        }, [setIsPresentationPoppedOut]);

        if (status === StreamStatus.External) {
            return (
                <PopOutWindow
                    onUnload={disableDualScreenMode}
                    remotePopOut={remotePopOut}
                    title={t(
                        'media.presentation.pexip-presentation',
                        'Presentation Window',
                    )}
                >
                    {isPresenting ? (
                        <ExternalVideo
                            handleVideoClick={handleVideoClick}
                            isFullHeight
                            isFullWidth
                            isPresentation
                            mediaStream={
                                localIsPresenting
                                    ? localMediaStream
                                    : remoteIsPresenting
                                      ? remoteMediaStream
                                      : undefined
                            }
                            onPictureInPictureChange={
                                setIsPresentationBrowserPip
                            }
                            status={status}
                            isReceivingVideo={props.isReceivingVideo}
                        />
                    ) : (
                        <PoppedOutEmptyPresentation
                            onClose={disableDualScreenMode}
                        />
                    )}
                </PopOutWindow>
            );
        }
        return (
            <DraggableMeetingVideo
                draggableAriaLabel={draggableAriaLabel}
                bottomStartContent={
                    presenterText ? (
                        <PresenterLabel text={presenterText} />
                    ) : undefined
                }
                ref={ref}
                handleVideoClick={handleVideoClick}
                isPresentation
                mediaStream={
                    localIsPresenting
                        ? localMediaStream
                        : remoteIsPresenting
                          ? remoteMediaStream
                          : undefined
                }
                onDoubleClick={toggleFullScreen}
                onPictureInPictureChange={setIsPresentationBrowserPip}
                status={status}
                isSidePanelVisible={isSidePanelVisible}
                floatRoot={floatRoot}
                videoElementId={videoElementId}
                tabIndex={tabIndex}
                onKeyDown={onKeyDown}
                {...props}
            />
        );
    },
);

PresentationMeetingVideo.displayName = 'PresentationMeetingVideo';
